import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { teamAPI, getImageUrl } from "../../services/api";
import { useBookingModal } from "../../context/BookingModalContext";
import Reveal from "../Shared/Reveal";
import "./TeamDetail.scss";

const TeamDetail = () => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openModal } = useBookingModal();

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await teamAPI.getById(id);
        setMember(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Team member not found" : "Failed to load team member");
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id]);

  if (loading) {
    return (
      <div className="team-detail-loading">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="team-detail-error">
        <div className="error-message">
          <h2>Team Member Not Found</h2>
          <p>{error || "The team member you are looking for does not exist."}</p>
          <Link to="/our-team" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Back to Our Team</span>
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = member.bio
    ? member.bio.split(/\n+/).filter(Boolean)
    : [];

  return (
    <div className="main main_team_detail">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">MEET THE TEAM</span>
          <h1 className="headline__text">{member.name}</h1>
          {member.role && <p className="page-hero__subtitle">{member.role}</p>}
        </div>
      </div>

      <div className="team-detail-section">
        <div className="team-detail-container">
          <Link to="/our-team" className="back-to-team">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Our Team
          </Link>

          <div className="team-detail-layout">
            <Reveal as="div" direction="fade" className="team-detail-image">
              {member.image ? (
                <img
                  src={getImageUrl(member.image)}
                  alt={member.name}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.classList.add("team-detail-image--placeholder");
                  }}
                />
              ) : (
                <div className="team-detail-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
            </Reveal>

            <Reveal as="div" direction="up" delay={0.1} className="team-detail-content">
              {member.qualifications && (
                <p className="team-detail-qualifications">{member.qualifications}</p>
              )}
              {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => <p key={i}>{para}</p>)
              ) : (
                <p>{member.name} is part of our dedicated team of healthcare professionals, committed to providing the highest quality, personalised care.</p>
              )}
              {(member.email || member.phone) && (
                <div className="team-detail-contact">
                  {member.email && (
                    <a href={`mailto:${member.email}`}>{member.email}</a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone.replace(/\D/g, '')}`}>{member.phone}</a>
                  )}
                </div>
              )}
            </Reveal>
          </div>

          <Reveal as="div" direction="up" className="team-detail-cta">
            <h2>Ready to book with {member.name.split(" ")[0]}?</h2>
            <p>Book an appointment and let our team build a personalised treatment plan just for you.</p>
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Book an Appointment</span>
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default TeamDetail;
